import React, { useState, useEffect } from "react";
import {
  Modal,
  TextField,
  Typography,
  TextDescription,
  Select,
} from "@ann/web-components";
import { observer } from "mobx-react";
import {
  ModalBodyContent,
  ParentDepartmentBox,
  ErrorMessage,
} from "./CreateSubDepartmentModal.styled";
import { useUsersForDepartments } from "../../../../../../../context";

const CreateSubDepartmentModal = ({
  size = "medium",
  isOpen,
  onClose,
  onCreateSubDepartment,
  parentDepartmentName = "",
  existingNames = [],
}) => {
  const { adminOwnerOptions, loading: loadingUsers, fetchUsers } = useUsersForDepartments();
  const [subDepartmentName, setSubDepartmentName] = useState("");
  const [description, setDescription] = useState("");
  const [adminOwner, setAdminOwner] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showDuplicateError, setShowDuplicateError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchUsers({ page_size: 50 });
    }
  }, [isOpen, fetchUsers]);

  const isDuplicateName =
    subDepartmentName.trim().length > 0 &&
    existingNames.some(
      (name) =>
        name.toLowerCase().trim() === subDepartmentName.toLowerCase().trim()
    );
  const isFormValid = subDepartmentName.trim().length > 0;

  const handleSave = async () => {
    if (isDuplicateName) {
      setShowDuplicateError(true);
      return;
    }
    if (!isFormValid) return;

    try {
      setIsCreating(true);
      const subDepartmentData = {
        name: subDepartmentName,
        description,
        adminOwner,
        adminOwnerName: adminOwner?.label?.split(" - ")[0] || "",
      };
      await onCreateSubDepartment?.(subDepartmentData);
    } catch (error) {
      console.error("Error creating sub-department:", error);
    } finally {
      setIsCreating(false);
      handleClose();
    }
  };

  const handleClose = () => {
    setSubDepartmentName("");
    setDescription("");
    setAdminOwner(null);
    setIsCreating(false);
    setShowDuplicateError(false);
    onClose();
  };

  const handleNameChange = (value) => {
    setSubDepartmentName(value);
    if (showDuplicateError) setShowDuplicateError(false);
  };

  const handleAdminOwnerChange = (value) => {
    const selectedOption = adminOwnerOptions.find(
      (option) => option.value === value
    );
    setAdminOwner(selectedOption || null);
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width="900px" size={size}>
      <Modal.Header>
        <Modal.Header.Title>Create Sub-Department</Modal.Header.Title>
        <Modal.Header.Description>
          <Typography
            category="body"
            size="medium"
            weight="regular"
            color="body"
          >
            Add a sub-department and assign an owner. You can edit details
            later.
          </Typography>
        </Modal.Header.Description>
      </Modal.Header>
      <Modal.Body>
        <ModalBodyContent>
          <div>
            <Typography category="label" size="small" weight="bold">
              Parent Department
            </Typography>
            <ParentDepartmentBox>
              <Typography category="body" size="medium" weight="regular">
                {parentDepartmentName}
              </Typography>
            </ParentDepartmentBox>
          </div>

          <div>
            <TextField
              label="Sub-department name"
              size="small"
              placeholder="e.g., Sales - North America"
              value={subDepartmentName}
              onChange={handleNameChange}
              required={true}
              error={showDuplicateError && isDuplicateName}
            />
            {showDuplicateError && isDuplicateName && (
              <ErrorMessage>
                A sub-department with this name already exists
              </ErrorMessage>
            )}
          </div>

          <TextDescription
            height="148px"
            label="Description"
            value={description}
            onChange={(value) => setDescription(value)}
            placeholder="Describe the department's purpose and scope..."
            size="small"
          />

          <Select
            label="Admin Owner"
            value={adminOwner || undefined}
            onChange={handleAdminOwnerChange}
            placeholder={loadingUsers ? "Loading users..." : "Select admin owner"}
            size="small"
            width="100%"
            disabled={loadingUsers}
          >
            <Select.Menu>
              {adminOwnerOptions.length > 0 ? (
                adminOwnerOptions.map((option) => (
                  <Select.Item
                    key={option.value}
                    label={option.label}
                    value={option.value}
                  >
                    {option.label}
                  </Select.Item>
                ))
              ) : (
                <Select.Item label="No users available" value="" disabled>
                  No users available
                </Select.Item>
              )}
            </Select.Menu>
          </Select>
        </ModalBodyContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Modal.Button
          label="Cancel"
          onClick={handleClose}
          type="secondary"
          disabled={isCreating}
        />
        <Modal.Button
          label={isCreating ? "Creating..." : "Create Sub-Department"}
          onClick={handleSave}
          disabled={!subDepartmentName.trim() || isCreating}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default observer(CreateSubDepartmentModal);
